import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { updateUiState } from 'redux/slices/uiSlice';

/**
 * The Dashboard layout's responsive breakpoints and sidebar open/close state,
 * plus the mirror of the expanded flag into ui state that the sidebar and
 * top nav read.
 *
 * Split out of `Dashboard.js` in Phase 6; called unconditionally, so the
 * state and both effects keep the lifetime they had inline.
 *
 * `open` is deliberately `false` at `md` and up: at that width the permanent
 * sidebar is always rendered, so the temporary drawer must never open.
 */
const useDashboardSidebar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navCollapsedByDefault = useSelector(
    (state) => state.uiStates.navCollapsedByDefault,
  );
  const twoLevelNav = useSelector((state) => state.uiStates.twoLevelNav);
  const navSidebarExpanded = useSelector(
    (state) => state.uiStates.navSidebarExpanded,
  );
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const isXs = useMediaQuery(theme.breakpoints.only('xs'), {
    defaultMatches: false,
  });

  const [openSidebar, setOpenSidebar] = useState(false);
  const [showBottomNav, setShowBottomNav] = useState(false);

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };

  const open = isMd ? false : openSidebar;

  useEffect(() => {
    if (isXs) {
      setShowBottomNav(true);
    } else {
      setShowBottomNav(false);
    }
  }, [isXs]);

  // From md up the sidebar starts as the "Keep the navigation bar collapsed"
  // preference says: collapsed to the rail (with no toggle to open it), or,
  // with the preference off, the way the toggle last left it
  // (`navSidebarExpanded`, persisted across reloads). The preference pins
  // only the two-level nav's rail; the one-level sidebar always has its
  // toggle, so it always follows `navSidebarExpanded`. `open` is always false
  // at md.
  const pinnedCollapsed =
    Boolean(twoLevelNav) && navCollapsedByDefault !== false;
  useEffect(() => {
    if (isMd) {
      dispatch(
        updateUiState({
          key: 'dashboardSidebarExpanded',
          value: !pinnedCollapsed && navSidebarExpanded !== false,
        }),
      );
    } else {
      dispatch(
        updateUiState({
          key: 'dashboardSidebarExpanded',
          value: open ? true : false,
        }),
      );
    }
  }, [isMd, open, pinnedCollapsed, navSidebarExpanded]);

  return {
    isMd,
    isXs,
    open,
    showBottomNav,
    handleSidebarOpen,
    handleSidebarClose,
  };
};

export default useDashboardSidebar;
